import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  faUserSecret,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { searchIcon, SVGIcon } from '@progress/kendo-svg-icons';
import {
  BehaviorSubject,
  takeUntil
} from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { AuthStore } from 'src/app/stores/auth.store';
import {
  dialogAnimationDuration,
  dialogTimer,
} from '../../constants/dialog-specs';
import { allImpersonateUsersPageSize } from '../../enums/notification';
import { ApiResponse } from '../../interfaces/api-response';
import { IImpersonateGetUserPayload } from '../../interfaces/impersonate-get-user-payload';
import {
  IImpersonateUser,
  IImpersonateUsersResponse,
} from '../../interfaces/impersonate-users-response';
import { validateEmail, validateName } from '../../validators/validator';
import { AbstractComponent } from '../abstract-component';

@Component({
  selector: 'app-impersonate-user',
  templateUrl: './impersonate-user.component.html',
  styleUrls: ['./impersonate-user.component.scss'],
  standalone: false,
})
export class ImpersonateUserComponent
  extends AbstractComponent
  implements OnInit {
  usersToImpersonate: IImpersonateUsersResponse;
  allUsersToImpersonate: any[] = [];
  loadingUsers: boolean = false;
  @Output() closeImpersonateDialog: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  public searchIcon: SVGIcon = searchIcon;
  selectedUser: IImpersonateUser;
  skip: number = 0;
  public pageSize: number = allImpersonateUsersPageSize;
  readonly defaultTotal: number = 0;
  public searchValue: string = '';
  public gridView: GridDataResult;
  private search$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public faUserSecret: SVGIcon = this.customFaIcon(faUserSecret);
  form: FormGroup;
  showSearchError: boolean;

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private authStore: AuthStore,
    private notificationService: NotificationService,
    private fb: FormBuilder,
  ) {
    super();
    // this.loadingUsers = true;
    this.initForm();
  }

  initForm(): void {
    this.form = this.fb.group({
      firstName: [''],
      lastName: ['', validateName(false)],
      email: ['', validateEmail(false)],
    });
  }

  ngOnInit(): void {
    // this.handleMutualExclusion();
  }
  getDistinctRoles(roles: string[]): string[] {
    return Array.from(new Set(roles));
  }

  // public handleActionItemsSearch(query: string): void {
  //   this.skip = 0; // reset paging
  //   this.search$.next(query);
  // }

  closeDialog(): void {
    this.closeImpersonateDialog.emit(false);
  }

  getUsersToImpersonate(): void {
    this.showSearchError = !this.form?.value?.firstName && !this.form?.value?.lastName &&
      !this.form?.value?.email;
    if (this.showSearchError || this.form?.invalid) {
      this.form.setErrors({ incorrect: true });
      return;
    } else {
      const payload: IImpersonateGetUserPayload = {
        pageSize: this.pageSize,
        pageNumber: Math.floor(this.skip / this.pageSize),
        email: this.form?.value?.email || null,
        firstName: this.form?.value?.firstName || null,
        lastName: this.form?.value?.lastName || null,
      };
      this.loadingUsers = true;
      this.userService.getUsersToImpersonate(payload).pipe(
        takeUntil(this.componentDestroyed)).subscribe((users: IImpersonateUsersResponse) => {
          this.loadingUsers = false;
          this.gridView = {
            data: users?.response,
            total: users?.totalCount ?? this.defaultTotal,
          };
          this.cdr?.markForCheck();
        });
    }
  }

  impersonate(dataItem: IImpersonateUser): void {
    this.loadingUsers = true;
    this.userService
      .impersonateUser(dataItem?.userName)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: (res: ApiResponse<any>) => {
          if (res?.isRequestSucessful && res?.value) {
            sessionStorage.setItem('impersonationToken', res?.value);
            this.authStore.startImpersonation(res);
            this.loadingUsers = false;
            this.closeDialog();
            location.reload();
          }
          if (!res?.isRequestSucessful) {
            this.loadingUsers = false;
            this.cdr.markForCheck();
            this.notificationService.show({
              content: 'Impersonation could not be started.',
              hideAfter: dialogTimer,
              cssClass: 'notification-error',
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'error', icon: true },
            });
          }
          if (res?.isRequestSucessful && !res?.value) {
            this.loadingUsers = false;
            this.cdr.markForCheck();
            this.notificationService.show({
              content:
                'Impersonation could not be started. Token was not created.',
              hideAfter: dialogTimer,
              cssClass: 'notification-error',
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'error', icon: true },
            });
          }
        },
        error: () => {
          this.loadingUsers = false;
          this.cdr.markForCheck();
          this.notificationService.show({
            content: 'Impersonation could not be started.',
            hideAfter: dialogTimer,
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  override afterOnDestroy(): void {
    // Cleanup if necessary
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;

    this.getUsersToImpersonate();
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,

      content: `<path d='${icon.icon[4]}' />`,

      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  private handleMutualExclusion(): void {
    const firstName = this.form.get('firstName');
    const lastName = this.form.get('lastName');
    const loginId = this.form.get('email');

    loginId?.valueChanges.subscribe(value => {
      if (value) {
        firstName?.disable({ emitEvent: false });
        lastName?.disable({ emitEvent: false });
      } else {
        firstName?.enable({ emitEvent: false });
        lastName?.enable({ emitEvent: false });
      }
    });

    const toggleLoginId = () => {
      const hasName =
        firstName?.value || lastName?.value;

      if (hasName) {
        loginId?.disable({ emitEvent: false });
      } else {
        loginId?.enable({ emitEvent: false });
      }
    };

    firstName?.valueChanges.subscribe(toggleLoginId);
    lastName?.valueChanges.subscribe(toggleLoginId);
  }
}
